import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getCreditInfoByStore } from '../../api/credit.api';
import { getMyPaymentStatus, type LicensePaymentStatus } from '../../api/license.api';
import styles from './LicensePaymentBanner.module.css';

/**
 * 라이선스 무통장입금 안내 배너
 *
 * 표시 조건:
 *   - paymentStatus === 'PENDING_PAYMENT' 일 때만 노출
 *   - PAID / WAIVED / LEGACY 는 표시 X (자동 숨김)
 *   - LOCKED 는 별도 LockedScreen 에서 처리
 *
 * 보안:
 *   - 입금 계좌/금액은 백엔드 환경변수에서 받아옴 (프론트 하드코딩 X)
 *   - 모든 정보는 GET /api/licenses/:id/payment-status 응답
 */

function formatRemaining(ms: number): string {
  if (ms <= 0) return '0분';
  const totalMin = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  if (hours > 0) {
    return `${hours}시간 ${minutes}분`;
  }
  return `${minutes}분`;
}

function copyToClipboard(text: string, label: string): void {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    navigator.clipboard.writeText(text).then(() => {
      // 안내 토스트 대신 alert (단순)
      window.alert(`${label} 복사됨: ${text}`);
    });
  }
}

export default function LicensePaymentBanner() {
  const { storeId, isDemo } = useAuth();
  const [status, setStatus] = useState<LicensePaymentStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    if (!storeId || isDemo) {
      setIsLoading(false);
      return;
    }
    try {
      const info = await getCreditInfoByStore(storeId);
      const data = await getMyPaymentStatus(info.licenseId);
      setStatus(data);
    } catch {
      // 에러 시 배너 표시 안 함 (기능 차단 X)
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [storeId, isDemo]);

  useEffect(() => {
    load();
    // 1분마다 재조회 (입금 확인 반영)
    const refresh = setInterval(load, 60_000);
    return () => clearInterval(refresh);
  }, [load]);

  // 카운트다운 갱신 (1초)
  useEffect(() => {
    if (!status?.isPending) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [status?.isPending]);

  if (isLoading || !status) return null;
  // 결제 완료/면제/레거시 → 배너 숨김
  if (status.isPaid) return null;
  if (status.paymentStatus === 'REFUNDED') return null;
  // LOCKED 는 LockedScreen 에서 처리하므로 여기서는 숨김
  if (status.isLocked) return null;
  // PENDING_PAYMENT 만 여기서 처리
  if (!status.isPending) return null;

  const dueAtMs = status.paymentDueAt ? new Date(status.paymentDueAt).getTime() : 0;
  const remainingMs = Math.max(0, dueAtMs - now);
  const isUrgent = remainingMs < 6 * 60 * 60 * 1000; // 6시간 미만

  return (
    <div className={`${styles.banner} ${isUrgent ? styles.urgent : ''}`}>
      <div className={styles.header}>
        <div className={styles.title}>
          라이선스 무통장입금 안내
        </div>
        <div className={styles.countdown}>
          남은 시간: <strong>{formatRemaining(remainingMs)}</strong>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.message}>
          가입 후 48시간 무료 체험 중입니다. 계속 사용하시려면 아래 계좌로 입금해주세요.
          입금 확인 후 라이선스가 영구 활성화되며, 입금이 확인되지 않으면 잠금 상태로 전환됩니다.
        </div>

        <div className={styles.infoGrid}>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>은행</span>
            <span className={styles.infoValue}>{status.paymentInfo.bankName}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>계좌번호</span>
            <button
              type="button"
              className={styles.copyBtn}
              onClick={() => copyToClipboard(status.paymentInfo.accountNumber, '계좌번호')}
              title="클릭하여 복사"
            >
              {status.paymentInfo.accountNumber}
              <span className={styles.copyHint}>복사</span>
            </button>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>예금주</span>
            <span className={styles.infoValue}>{status.paymentInfo.accountHolder}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>금액</span>
            <span className={styles.infoValue}>
              <strong>{status.paymentInfo.amount.toLocaleString()}원</strong>
            </span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>입금자명</span>
            <button
              type="button"
              className={styles.copyBtn}
              onClick={() =>
                copyToClipboard(status.paymentInfo.depositorNameHint, '입금자명')
              }
              title="매장명과 정확히 일치하도록 입금. 클릭하여 복사"
            >
              {status.paymentInfo.depositorNameHint}
              <span className={styles.copyHint}>복사</span>
            </button>
          </div>
        </div>

        <div className={styles.note}>
          입금 확인이 늦어질 경우 고객센터(010-3000-6253) 또는
          mansuk@sangsangnode.co.kr 로 연락 부탁드립니다.
        </div>
      </div>
    </div>
  );
}
